import React, { useState, useEffect, useRef } from 'react';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Plus, Edit, Trash2, Search, Filter, RefreshCw, 
    AlertCircle, CheckCircle, X, FolderOpen, MoreVertical
} from 'lucide-react';
import notify from './Nots.jsx';

const CategoryForm = ({ onAddOrUpdate, name, setName, editingId, setEditingId, inputRef, handleCancelEdit, postRequest, putRequest, setLoading }) => {
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = 'Category name is required';
        } else if (name.trim().length < 2) {
            newErrors.name = 'Category name must be at least 2 characters';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setLoading(true);
        try {
        if (editingId) {
                const formData = new FormData();
                formData.append("name", name.trim());
            const response = await putRequest(`${domain}/event-categories/${editingId}`, formData, true);
            setEditingId(null);
            onAddOrUpdate(response.body);
                notify("Category updated successfully", "success");
        } else {
                const formData = new FormData();
                formData.append("name", name.trim());
            const response = await postRequest(`${domain}/event-categories`, formData, true);
            onAddOrUpdate(response.body);
                notify("Category added successfully", "success");
            }
            setName('');
            setErrors({});
        } catch (error) {
            notify("Failed to save category", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setName("");
        setEditingId(null);
        setErrors({});
        inputRef.current?.focus();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-4 md:mb-6"
        >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FolderOpen className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                        {editingId ? 'Edit Category' : 'Add New Category'}
                    </h3>
                    <p className="text-sm text-gray-600">
                        {editingId ? 'Update the category information' : 'Create a new event category'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Name
                    </label>
                    <div className="relative">
            <input
                ref={inputRef}
                type="text"
                value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (errors.name) setErrors({ ...errors, name: '' });
                            }}
                            placeholder="Enter category name..."
                            className={`w-full px-3 py-2 md:px-4 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm ${
                                errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                        />
                        {errors.name && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                            >
                                <AlertCircle className="w-4 h-4" />
                                {errors.name}
                            </motion.div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                    <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 !text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm"
                    >
                        {editingId ? (
                            <>
                                <Edit className="w-4 h-4" />
                                Update Category
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" />
                                Add Category
                            </>
                        )}
                    </button>
                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 md:px-6 md:py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 text-sm"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    )}
                </div>
        </form>
        </motion.div>
    );
};

const CategoryList = ({ categories, onDelete, onEdit, searchTerm, setSearchTerm, loading }) => {
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedCategories = [...filteredCategories].sort((a, b) => {
        const aValue = a[sortBy]?.toLowerCase() || '';
        const bValue = b[sortBy]?.toLowerCase() || '';
        return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
    });

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    if (loading) {
    return (
            <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
            >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Categories Found</h3>
                <p className="text-gray-600 mb-6">Start by adding your first event category</p>
            </motion.div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search and Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={`${sortBy}-${sortOrder}`}
                            onChange={(e) => {
                                const [field, order] = e.target.value.split('-');
                                setSortBy(field);
                                setSortOrder(order);
                            }}
                            className="px-3 py-2 md:px-4 md:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="id-asc">ID (Low-High)</option>
                            <option value="id-desc">ID (High-Low)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between text-sm text-gray-600">
                <p>Showing {filteredCategories.length} of {categories.length} categories</p>
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                    >
                        Clear search
                    </button>
                )}
            </div>

            {/* Categories List */}
            <div className="space-y-2 md:space-y-3">
                <AnimatePresence>
                    {sortedCategories.map((category, index) => (
                        <motion.div
                            key={category.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 hover:shadow-md transition-all duration-200"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center gap-3 md:gap-4">
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                                        <FolderOpen className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 text-sm md:text-base">{category.name}</h4>
                                        <p className="text-xs md:text-sm text-gray-500">ID: {category.id}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => onEdit(category)}
                                        className="px-3 py-2 md:px-4 md:py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-1 md:gap-2 text-sm"
                                    >
                                        <Edit className="w-4 h-4" />
                                        <span className="hidden sm:inline">Edit</span>
                                    </button>
                                    <button
                                        onClick={() => onDelete(category.id)}
                                        className="px-3 py-2 md:px-4 md:py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-1 md:gap-2 text-sm"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span className="hidden sm:inline">Delete</span>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};

const CategoryComponent = () => {
    const { setLoading } = useLoadingContext();
    const inputRef = useRef();
    const [name, setName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLocalLoading] = useState(false);
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLocalLoading(true);
        try {
            const response = await getRequest(`${domain}/event-categories`);
            setCategories(response.body || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            notify("Failed to load categories", "error");
        } finally {
            setLocalLoading(false);
        }
    };

    const handleAddOrUpdate = (category) => {
        const index = categories.findIndex((c) => c.id === category.id);
        if (index === -1) {
            setCategories([category, ...categories]);
        } else {
            const newCategories = [...categories];
            newCategories[index] = category;
            setCategories(newCategories);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this category? This action cannot be undone.")) {
            setLoading(true);
            try {
                await deleteRequest(`${domain}/event-categories/${id}`);
            setCategories(categories.filter((category) => category.id !== id));
                notify("Category deleted successfully", "success");
            } catch (error) {
                notify("Failed to delete category", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (category) => {
        setName(category.name);
        setEditingId(category.id);
        inputRef.current?.focus();
    };

    const handleCancelEdit = () => {
        setName("");
        setEditingId(null);
        inputRef.current?.focus();
    };

    return (
        <div className="space-y-6">
            <CategoryForm 
                onAddOrUpdate={handleAddOrUpdate} 
                name={name} 
                setName={setName} 
                editingId={editingId} 
                setEditingId={setEditingId} 
                inputRef={inputRef} 
                handleCancelEdit={handleCancelEdit} 
                postRequest={postRequest} 
                putRequest={putRequest} 
                setLoading={setLoading} 
            />
            
            <CategoryList
                categories={categories}
                onDelete={handleDelete}
                onEdit={handleEdit}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loading={loading}
            />
        </div>
    );
};

export default CategoryComponent;